import { baseUrl } from '../environment/environment';
import {
    ArticleDto,
    BadRequestResponse,
    Client,
    GoodsDto,
    IClient,
    OrderDto,
    ReceiptDto,
    WaybillDto,
} from './ClientApi';
import { protectedResources } from '../auth/AuthConfig';
import { msalInstance } from '../auth/AuthProvider';

// Last successful body of every GET, keyed by URL, so an unchanged list is revalidated with
// If-None-Match and served from here on the backend's 304 instead of being rebuilt, gzipped and
// downloaded again (Cache API: per origin, survives reloads and PWA restarts).
const API_RESPONSE_CACHE = 'wh-api-responses-v1';

async function openResponseCache(): Promise<Cache | undefined> {
    try {
        return typeof caches === 'undefined' ? undefined : await caches.open(API_RESPONSE_CACHE);
    } catch {
        return undefined;
    }
}

export async function httpFetch(url: RequestInfo, init?: RequestInit): Promise<Response> {
    const allAccounts = msalInstance.getAllAccounts();

    const token = await msalInstance.acquireTokenSilent({
        scopes: protectedResources.warehouseApi.scopes,
        account: allAccounts[0],
    });

    const isGet = (init?.method ?? 'GET').toUpperCase() === 'GET';
    const cacheKey = typeof url === 'string' ? url : url.url;
    const cache = isGet ? await openResponseCache() : undefined;
    const cached = cache ? await cache.match(cacheKey) : undefined;
    const cachedETag = cached?.headers.get('ETag');

    const authenticatedInit: RequestInit = {
        ...init,
        headers: {
            ...init?.headers,
            Authorization: 'Bearer ' + token.accessToken,
            // The backend's ETag also encodes the user and roles, so a body cached for another
            // account on this browser never matches and is simply replaced.
            ...(cachedETag ? { 'If-None-Match': cachedETag } : {}),
        },
    };

    const response = await fetch(url, authenticatedInit);

    if (cache) {
        if (response.status === 304 && cached) {
            return cached;
        }

        if (response.ok && response.headers.get('ETag')) {
            cache.put(cacheKey, response.clone()).catch(() => undefined);
        }
    }

    return response;
}

function WarehouseClientApi() {
    const clientApi = new Client(baseUrl, {
        fetch: httpFetch,
    });

    return clientApi;
}

export const WarehouseClient: IClient = WarehouseClientApi();

export interface BulkInstallGoodsItem {
    articleId: number;
    quantity: number;
}

export interface BulkInstallGoodsPayload {
    goodsOriginSiteId: number;
    usedOnSituationId?: number;
    executionTime?: Date;
    reason?: string;
    items: BulkInstallGoodsItem[];
}

export async function bulkInstallGoodsOnSite(payload: BulkInstallGoodsPayload): Promise<GoodsDto[]> {
    const url = baseUrl + '/api/Goods/useBulk';

    const response = await httpFetch(url, {
        body: JSON.stringify(payload),
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json-patch+json',
            Accept: 'text/plain',
        },
    });

    const text = await response.text();

    if (response.status === 200) {
        const data = text === '' ? null : JSON.parse(text);
        if (Array.isArray(data)) {
            return data.map((item: any) => GoodsDto.fromJS(item));
        }
        return [];
    }

    if (response.status === 400) {
        const data = text === '' ? null : JSON.parse(text);
        throw BadRequestResponse.fromJS(data);
    }

    throw new Error(`bulkInstallGoodsOnSite failed with status ${response.status}: ${text}`);
}

// --- Product templates (sastavnice) + pricing --------------------------------
// Endpoints live in ProductTemplateController (FinanceManager + CEO only). ClientApi.ts is
// NSwag-generated so these hand-written wrappers stay here per convention.

export interface ProductTemplateItemModel {
    id?: number;
    quantity: number;
    articleId: number;
    article?: ArticleDto;
}

export interface ProductTemplateModel {
    id?: number;
    title: string;
    description: string;
    items?: ProductTemplateItemModel[];
}

export interface TemplateCostItem {
    articleId: number;
    quantity: number;
    priceOverride?: number;
}

export interface TemplateCostRequest {
    asOf?: string; // ISO date-time; omit for "latest known price"
    items: TemplateCostItem[];
}

export interface TemplateCostLine {
    articleId: number;
    article?: ArticleDto;
    quantity: number;
    unitPrice?: number;
    unitPriceDate?: string;
    priceKnown: boolean;
    priceOverridden: boolean;
    lineTotal?: number;
}

export interface TemplateCostResponse {
    asOf: string;
    total: number;
    allPricesKnown: boolean;
    lines: TemplateCostLine[];
}

function jsonHeaders(): { 'Content-Type': string; Accept: string } {
    return { 'Content-Type': 'application/json', Accept: 'application/json' };
}

export async function getAllProductTemplates(): Promise<ProductTemplateModel[]> {
    const response = await httpFetch(baseUrl + '/api/ProductTemplate/all', {
        method: 'GET',
        headers: jsonHeaders(),
    });

    if (response.status !== 200) {
        throw new Error(`getAllProductTemplates failed with status ${response.status}`);
    }

    return (await response.json()) as ProductTemplateModel[];
}

export async function getProductTemplateById(id: number): Promise<ProductTemplateModel> {
    const response = await httpFetch(baseUrl + '/api/ProductTemplate/byId/' + id, {
        method: 'GET',
        headers: jsonHeaders(),
    });

    if (response.status !== 200) {
        throw new Error(`getProductTemplateById failed with status ${response.status}`);
    }

    return (await response.json()) as ProductTemplateModel;
}

export async function createProductTemplate(payload: ProductTemplateModel): Promise<ProductTemplateModel> {
    const response = await httpFetch(baseUrl + '/api/ProductTemplate', {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify(payload),
    });

    if (response.status === 400) {
        throw BadRequestResponse.fromJS(await response.json());
    }
    if (response.status !== 200) {
        throw new Error(`createProductTemplate failed with status ${response.status}`);
    }

    return (await response.json()) as ProductTemplateModel;
}

export async function updateProductTemplate(id: number, payload: ProductTemplateModel): Promise<ProductTemplateModel> {
    const response = await httpFetch(baseUrl + '/api/ProductTemplate/' + id, {
        method: 'PUT',
        headers: jsonHeaders(),
        body: JSON.stringify(payload),
    });

    if (response.status === 400) {
        throw BadRequestResponse.fromJS(await response.json());
    }
    if (response.status !== 200) {
        throw new Error(`updateProductTemplate failed with status ${response.status}`);
    }

    return (await response.json()) as ProductTemplateModel;
}

export async function deleteProductTemplate(id: number): Promise<void> {
    const response = await httpFetch(baseUrl + '/api/ProductTemplate/' + id, {
        method: 'DELETE',
        headers: jsonHeaders(),
    });

    if (response.status !== 200) {
        throw new Error(`deleteProductTemplate failed with status ${response.status}`);
    }
}

export async function calculateTemplateCost(request: TemplateCostRequest): Promise<TemplateCostResponse> {
    const response = await httpFetch(baseUrl + '/api/ProductTemplate/cost', {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify(request),
    });

    if (response.status === 400) {
        throw BadRequestResponse.fromJS(await response.json());
    }
    if (response.status !== 200) {
        throw new Error(`calculateTemplateCost failed with status ${response.status}`);
    }

    return (await response.json()) as TemplateCostResponse;
}

// --- Express (ubrzani) transfer order --------------------------------------------------------
// POST /api/Order/express: goods were already handed over on site, so the backend records the
// order, transfer receipt, waybill and reservation in one transaction and returns the order in
// Transport. Reception is confirmed through the regular "Preuzmi robu" step.

export interface ExpressOrderItem {
    articleId: number;
    quantity: number;
}

export interface ExpressOrderPayload {
    originSiteId: number;
    destinationSiteId: number;
    executionDate: Date;
    description: string;
    driverName?: string;
    driverUniqueNumber?: string;
    vehicleLicensePlateNum?: string;
    items: ExpressOrderItem[];
}

export async function createExpressOrder(payload: ExpressOrderPayload): Promise<OrderDto> {
    const response = await httpFetch(baseUrl + '/api/Order/express', {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({ ...payload, executionDate: payload.executionDate.toISOString() }),
    });

    if (response.status === 400) {
        throw BadRequestResponse.fromJS(await response.json());
    }
    if (response.status !== 200) {
        throw new Error(`createExpressOrder failed with status ${response.status}`);
    }

    return OrderDto.fromJS(await response.json());
}

// --- Atomic order workflow steps ----------------------------------------------------------
// POST /api/Order/{id}/approve | dispatch | receive | reject | cancel. Each call writes the
// document, moves the goods and changes the status in one backend transaction, so the client no
// longer chains 2-3 requests that could fail half-way. Every step returns the fresh order plus
// the goods rows it touched, ready to be merged into the store.

export interface OrderStepResult {
    order: OrderDto;
    goods: GoodsDto[];
}

async function postOrderStep(path: string, body?: object): Promise<OrderStepResult> {
    const response = await httpFetch(baseUrl + '/api/Order/' + path, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify(body ?? {}),
    });

    if (response.status === 400) {
        throw BadRequestResponse.fromJS(await response.json());
    }
    if (response.status !== 200) {
        throw new Error(`order step ${path} failed with status ${response.status}`);
    }

    const data = await response.json();

    return {
        order: OrderDto.fromJS(data.order),
        goods: Array.isArray(data.goods) ? data.goods.map((g: any) => GoodsDto.fromJS(g)) : [],
    };
}

/** Pending → Approved with the transfer receipt (origin warehouse owner). */
export function approveOrder(orderId: number, receipt: ReceiptDto): Promise<OrderStepResult> {
    return postOrderStep(orderId + '/approve', receipt);
}

/** Approved → Transport: waybill issued and goods reserved (origin warehouse owner). */
export function dispatchOrder(orderId: number, waybill: WaybillDto): Promise<OrderStepResult> {
    return postOrderStep(orderId + '/dispatch', waybill);
}

/** Transport → Delivered: reception receipt issued and goods land on the destination. */
export function receiveOrder(
    orderId: number,
    receipt: ReceiptDto,
    uniqueBatchId?: string,
    batchArrivalDate?: Date,
): Promise<OrderStepResult> {
    const query = new URLSearchParams();
    if (uniqueBatchId) {
        query.set('uniqueBatchId', uniqueBatchId);
    }
    if (batchArrivalDate) {
        query.set('batchArrivalDate', batchArrivalDate.toISOString());
    }
    const suffix = query.toString() ? '?' + query.toString() : '';

    return postOrderStep(orderId + '/receive' + suffix, receipt);
}

/** Transport → Returned: destination owner refuses the goods, they go back to the origin. */
export function rejectOrderStep(orderId: number): Promise<OrderStepResult> {
    return postOrderStep(orderId + '/reject');
}

/** Open status → Cancelled; goods in transit go back to the origin. */
export function cancelOrderStep(orderId: number): Promise<OrderStepResult> {
    return postOrderStep(orderId + '/cancel');
}
